import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Eye, Trash2, Star, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { cn } from '../../lib/utils';

const CATEGORIES = ["Value Prop", "Price Comparison", "Process Oriented", "Testimonials", "UGC/Lifestyle", "Product Based"];
const POST_TYPES = ["AJ Talking Head", "Reel", "Static", "Carousel"];

const categoryColors = {
  "Value Prop": "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",
  "Price Comparison": "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
  "Process Oriented": "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100",
  "Testimonials": "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
  "UGC/Lifestyle": "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100",
  "Product Based": "bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100",
};

const statusColors = {
  Draft: 'bg-zinc-100 text-zinc-600',
  'In Progress': 'bg-blue-50 text-blue-700',
  Review: 'bg-amber-50 text-amber-700',
  Completed: 'bg-emerald-50 text-emerald-700',
  Archived: 'bg-zinc-100 text-zinc-400',
};

const emptyForm = { title: '', overall_concept: '', category: 'Value Prop', post_type: 'Static' };

export default function AdsCreatives({ items, config, reload, hasFullAccess, user }) {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  const filtered = activeCategory === 'all'
    ? items
    : items.filter(c => c.category === activeCategory);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      setSaving(true);
      await api.post('/ads/creatives', form);
      toast.success('Creative idea added');
      setShowDialog(false);
      setForm(emptyForm);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ads/creatives/${id}`);
      toast.success('Deleted');
      setDeleteId(null);
      reload();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  // Count per category
  const counts = {};
  CATEGORIES.forEach(c => { counts[c] = items.filter(i => i.category === c).length; });

  return (
    <div className="space-y-5" data-testid="ads-creatives">
      {/* Category Filter Buttons */}
      <div className="flex flex-wrap gap-2" data-testid="creative-category-filters">
        <button
          onClick={() => setActiveCategory('all')}
          className={cn(
            "px-4 py-2 rounded-full text-sm font-medium border transition-all",
            activeCategory === 'all'
              ? "bg-zinc-900 text-white border-zinc-900"
              : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
          )}
          data-testid="filter-all"
        >
          All ({items.length})
        </button>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            data-testid={`filter-${cat.toLowerCase().replace(/[\s/]/g, '-')}`}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium border transition-all",
              activeCategory === cat
                ? categoryColors[cat]?.replace('hover:', '') + " ring-1 ring-offset-1"
                : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
            )}
          >
            {cat} ({counts[cat] || 0})
          </button>
        ))}
      </div>

      {/* Add Idea Button */}
      {hasFullAccess && (
        <div className="flex justify-end">
          <Button
            onClick={() => { setForm(emptyForm); setShowDialog(true); }}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="add-creative-btn"
          >
            <Plus size={16} className="mr-1.5" /> Add Idea
          </Button>
        </div>
      )}

      {/* Creative Cards Grid */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <p className="text-sm text-zinc-400">No creatives yet — add your first idea above</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creative-cards">
          {filtered.map(c => (
            <div
              key={c.id}
              className="bg-white border border-zinc-200 rounded-lg p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow"
              data-testid={`creative-card-${c.id}`}
            >
              {/* Top row: category + status */}
              <div className="flex items-center justify-between gap-2">
                <Badge variant="outline" className={cn("text-xs", categoryColors[c.category]?.split(' hover:')[0])}>
                  {c.category}
                </Badge>
                <Badge variant="secondary" className={cn("text-xs", statusColors[c.status])}>
                  {c.status}
                </Badge>
              </div>

              {/* Title */}
              <h3 className="text-sm font-semibold text-zinc-900 leading-snug">{c.title}</h3>

              {/* Concept preview */}
              {c.overall_concept && (
                <p className="text-xs text-zinc-500 line-clamp-2">{c.overall_concept}</p>
              )}

              {/* Meta row */}
              <div className="flex items-center gap-3 text-xs text-zinc-400">
                <span className="bg-zinc-100 px-2 py-0.5 rounded text-zinc-600">{c.post_type}</span>
                <span>{formatDate(c.created_at)}</span>
                {c.score && (
                  <span className="flex items-center gap-0.5 text-amber-600">
                    <Star size={12} fill="currentColor" /> {c.score}/10
                  </span>
                )}
                {c.roas != null && c.roas > 0 && (
                  <span className="flex items-center gap-0.5 text-emerald-600">
                    <TrendingUp size={12} /> {c.roas}x
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1 mt-auto border-t border-zinc-100">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs text-zinc-700 border-zinc-200"
                  onClick={() => navigate(`/ads/creatives/${c.id}`)}
                  data-testid={`view-creative-${c.id}`}
                >
                  <Eye size={14} className="mr-1" /> View Details
                </Button>
                {c.final_asset?.download_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs text-emerald-700 border-emerald-200 bg-emerald-50"
                    onClick={() => window.open(c.final_asset.download_url, '_blank')}
                    data-testid={`view-final-${c.id}`}
                  >
                    View Creative
                  </Button>
                )}
                {hasFullAccess && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-zinc-400 hover:text-red-600"
                    onClick={() => setDeleteId(c.id)}
                    data-testid={`delete-creative-${c.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Idea Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg bg-white text-zinc-900" data-testid="add-creative-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900">New Creative Idea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-700">Title</Label>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Ring vs Necklace comparison reel"
                className="mt-1 bg-white border-zinc-200 text-zinc-900"
                data-testid="creative-title-input"
              />
            </div>
            <div>
              <Label className="text-zinc-700">Overall Concept</Label>
              <Textarea
                value={form.overall_concept}
                onChange={e => setForm({ ...form, overall_concept: e.target.value })}
                placeholder="Describe the creative concept..."
                rows={3}
                className="mt-1 bg-white border-zinc-200 text-zinc-900"
                data-testid="creative-concept-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-700">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1 bg-white border-zinc-200 text-zinc-900" data-testid="creative-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-zinc-900">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-700">Type of Post</Label>
                <Select value={form.post_type} onValueChange={v => setForm({ ...form, post_type: v })}>
                  <SelectTrigger className="mt-1 bg-white border-zinc-200 text-zinc-900" data-testid="creative-posttype-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-zinc-900">
                    {POST_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="submit-creative-btn"
            >
              {saving ? 'Creating...' : 'Create Creative'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm bg-white text-zinc-900">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">Delete this creative?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">This will permanently delete the creative and all its files.</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 border-zinc-200 text-zinc-700" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button className="flex-1 bg-red-600 text-white hover:bg-red-700" onClick={() => handleDelete(deleteId)}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
